/**
 * UK COUNTIES — the one list, and which region each is in.
 *
 * ⚠️ THERE WERE THREE COPIES OF THIS, AND THEY HAD DRIFTED. Found 08/08/2026 while
 * folding Rob's note that the county control on Settings didn't match the one on
 * Locations. Both files carried 107 counties, and three were spelled differently:
 *
 *     Locations / this file      AddEditEmployeeDialog
 *     Durham                     County Durham
 *     Orkney Islands             Orkney
 *     Shetland Islands           Shetland
 *
 * ⚠️ THE SPELLINGS HERE WIN, and not because they are prettier. `COUNTY_REGION` below is
 * keyed by them, and it drives the *"this county is in Scotland — its bank holidays
 * differ"* hint on the Location editor. Renaming the keys would silently stop that hint
 * matching any county already stored. ("County Durham" is arguably the better name; the
 * cost of switching is a data migration for a label, which is not worth it today.)
 *
 * ⚠️ COUNTY IS AN ADDRESS LABEL, NOT A HOLIDAY INPUT (Rob, 02/08/2026). Nothing in the
 * backend reads it to decide anybody's leave — only `public_holiday_region` does. The
 * region hint below is a courtesy, not a rule, and it must stay one.
 */

export const ENGLAND_WALES_COUNTIES = [
  'Bedfordshire', 'Berkshire', 'Bristol', 'Buckinghamshire', 'Cambridgeshire', 'Cheshire',
  'City of London', 'Cornwall', 'Cumbria', 'Derbyshire', 'Devon', 'Dorset', 'Durham',
  'East Riding of Yorkshire', 'East Sussex', 'Essex', 'Gloucestershire', 'Greater London',
  'Greater Manchester', 'Hampshire', 'Herefordshire', 'Hertfordshire', 'Isle of Wight',
  'Kent', 'Lancashire', 'Leicestershire', 'Lincolnshire', 'Merseyside', 'Norfolk',
  'North Yorkshire', 'Northamptonshire', 'Northumberland', 'Nottinghamshire',
  'Oxfordshire', 'Rutland', 'Shropshire', 'Somerset', 'South Yorkshire', 'Staffordshire',
  'Suffolk', 'Surrey', 'Tyne and Wear', 'Warwickshire', 'West Midlands', 'West Sussex',
  'West Yorkshire', 'Wiltshire', 'Worcestershire',
  'Blaenau Gwent', 'Bridgend', 'Caerphilly', 'Cardiff', 'Carmarthenshire', 'Ceredigion',
  'Conwy', 'Denbighshire', 'Flintshire', 'Gwynedd', 'Isle of Anglesey', 'Merthyr Tydfil',
  'Monmouthshire', 'Neath Port Talbot', 'Newport', 'Pembrokeshire', 'Powys',
  'Rhondda Cynon Taf', 'Swansea', 'Torfaen', 'Vale of Glamorgan', 'Wrexham',
];

export const SCOTLAND_COUNTIES = [
  'Aberdeenshire', 'Angus', 'Argyll and Bute', 'Clackmannanshire',
  'Dumfries and Galloway', 'Dundee', 'East Ayrshire', 'East Dunbartonshire',
  'East Lothian', 'East Renfrewshire', 'Edinburgh', 'Falkirk', 'Fife', 'Glasgow',
  'Highland', 'Inverclyde', 'Midlothian', 'Moray', 'North Ayrshire', 'North Lanarkshire',
  'Orkney Islands', 'Perth and Kinross', 'Renfrewshire', 'Scottish Borders',
  'Shetland Islands', 'South Ayrshire', 'South Lanarkshire', 'Stirling',
  'West Dunbartonshire', 'West Lothian', 'Western Isles',
];

export const NI_COUNTIES = [
  'Antrim', 'Armagh', 'Down', 'Fermanagh', 'Londonderry', 'Tyrone',
];

export const UK_COUNTIES = [
  ...ENGLAND_WALES_COUNTIES, ...SCOTLAND_COUNTIES, ...NI_COUNTIES,
].sort((a, b) => a.localeCompare(b));

// ⚠️ RE-EXPORTED, NOT DECLARED. The region vocabulary moved to `lib/regions.js` on
// 13/08/2026 when it turned out four files each had their own and two disagreed. This
// re-export exists so the screens that reach for it alongside the county list keep
// working — it is the same object, not a copy.
export { REGION_LABELS } from './regions.js';

/** Which public holiday region a county sits in — for the advisory hint only. */
export const COUNTY_REGION = {};
ENGLAND_WALES_COUNTIES.forEach((c) => { COUNTY_REGION[c] = 'england-and-wales'; });
SCOTLAND_COUNTIES.forEach((c) => { COUNTY_REGION[c] = 'scotland'; });
NI_COUNTIES.forEach((c) => { COUNTY_REGION[c] = 'northern-ireland'; });

/**
 * The counties to OFFER for a country — a controlled list for the UK, nothing elsewhere.
 *
 * ⚠️ DELIBERATELY EMPTY OUTSIDE THE UK, and the reasoning is worth keeping: ISO's GB
 * subdivisions are 221 councils and unitary authorities with no West Midlands, Merseyside
 * or Greater Manchester in them, so adopting one "proper" worldwide vocabulary would make
 * the one list that works today worse. The UK keeps its postal counties; everywhere else
 * types their own word for it.
 */
export const countiesFor = (countryCode) =>
  (countryCode === 'GB' ? UK_COUNTIES : []);
